'use client';

import type { PostTableColumnAt, PostTableColumnLayout } from 'src/utils/moments/view/post-table-columns';

import React, { useMemo, useState, useEffect, useCallback } from 'react';

import CachedIcon from '@mui/icons-material/Cached';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  Button,
  Dialog,
  Tooltip,
  Checkbox,
  TableRow,
  Accordion,
  TableBody,
  TableCell,
  TableHead,
  IconButton,
  Typography,
  DialogTitle,
  DialogActions,
  DialogContent,
  TableContainer,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import { removeHtmlTags } from 'src/utils/text/html-text';
import { SORT_TYPES } from 'src/utils/moments/view/post-sort';
import { countryLabel } from 'src/utils/platform/channel-label';
import { highlightTextWithHtmlRemoval } from 'src/utils/text/text-highlight';
import { formatNumber, formatTimestamp } from 'src/utils/format/format-utils';
import { displayAuthorName } from 'src/utils/moments/view/post-author-display';
import { getMetricsByScoreTime } from 'src/utils/metrics/post-metric-snapshot';
import { readNegativeEvidence } from 'src/utils/moments/view/negative-evidence';
import { engagementRatePct, ENGAGEMENT_RATE_FORMULA } from 'src/utils/metrics/engagement-rate';
import { getMetricAtDay, VIEW_DAY_COLUMNS, METRIC_DAY_DETAIL_OPTIONS } from 'src/utils/metrics/metric-day-detail';
import {
  sortPostsForTable,
  dayMetricSortField,
  getLatestDateMetrics,
  readPostTableRawMetrics,
  normalizePostTimestampMs,
} from 'src/utils/moments/view/post-table-sort';
import {
  postTableExtraColumnId,
  postTableColumnCatalog,
  resolvePostTableColumns,
  postTableDayColumnMetric,
  postTableDefaultHiddenIds,
  POST_TABLE_METRIC_COLUMNS,
  postTableReactionColumnField,
} from 'src/utils/moments/view/post-table-columns';

import { RADIUS } from 'src/theme/ui-tokens';

import { Iconify } from 'src/components/iconify';
import { PostMemoCell } from 'src/components/cmui/post-memo';
import SafeHtml from 'src/components/cmui/post-card/SafeHtml';
import { PostCardBookmarkOverlay } from 'src/components/cmui/bookmark';
import { PostCardCampaignOverlay } from 'src/components/cmui/campaign';
import HighlightedText from 'src/components/cmui/post-card/HighlightedText';

import PostDetail from './PostDetail';
import PostKindChips from './PostKindChips';
import { AccountAvatar } from '../account-avatar';
import PostMediaDetail from './post-media-detail';


/** `dense` 표 — 칸 좌우 여백 8px(기본 16px) · 본문 칸 위아래 8px. 열 최소 폭도 줄인다(머리 칸 `minWidth: dense ? …`) */
const DENSE_TABLE_SX = { '& .MuiTableCell-root': { px: 1 }, '& .MuiTableBody-root .MuiTableCell-root': { py: 1 } } as const;

/** 반응 지표 열 다섯 — 헤더·셀·검색 일치·빈 줄 colSpan 이 같은 목록을 본다 */
const METRIC_COLUMNS = POST_TABLE_METRIC_COLUMNS;

const NO_HIDDEN_METRIC_FIELDS: ReadonlySet<string> = new Set();

const NO_COLUMN_IDS: readonly string[] = [];

/** 앞 열도 일차 묶음인 일차 묶음의 첫 칸 — 두 묶음 사이 세로선 */
const DAY_GROUP_EDGE_SX = { borderLeft: '1px solid', borderLeftColor: 'divider' } as const;

/** 머리 둘째 줄(1·3·7·30일) — 테마가 마지막 줄 칸 선을 투명하게 칠하는 것(`:last-of-type`)을 같은 선택자로 덮어 머리 밑선을 잇는다 */
const DAY_HEAD_ROW_SX = { '&:last-of-type > .MuiTableCell-root': { borderBottomColor: 'divider' } } as const;

export type { PostTableColumnAt, PostTableColumnLayout };

/** 표 오른쪽 끝에 붙는 열 — 부르는 쪽이 칸을 그린다(`render`) */
export type PostTableExtraColumn = {
  key: string;
  label: string;
  /** 머리에 올리면 보이는 전체 글 */
  title?: string;
  minWidth?: number;
  render: (post: any) => React.ReactNode;
  /** 이 열만의 자리 — 없으면 표의 `extraColumnsAt`. `afterChannel` 은 채널 바로 뒤(계정 앞) · `afterView` 는 조회수 바로 뒤(E/R 앞) */
  at?: PostTableColumnAt;
  /** 처음엔 꺼진 열 — `columnLayout` 이 이 열을 모르면 숨긴다(「열 설정」 목록도 같은 값을 받아야 맞는다) */
  defaultHidden?: boolean;
  /** 「열 설정」의 묶음 열쇠 — 표에는 영향이 없고 목록(`postTableColumnCatalog`)과 맞추려고 받는다 */
  group?: string;
  /** 「열 설정」 줄 이름 — 없으면 `label`(머리가 번호 같은 줄임일 때) */
  menuLabel?: string;
  /** 머리를 눌러 정렬할 때 넘길 값 — `sortControl` 이 있고 그 `sortable` 에 들 때만 누를 수 있다 */
  sortField?: string;
  /** true 면 칸을 눌러도 줄 누름(상세)으로 간다 — 없으면 칸 누름을 줄로 올리지 않는다(칸 안 단추용) */
  rowClick?: boolean;
  /** 머리·칸·합계 칸의 가로 맞춤 — 숫자 열은 `right` */
  align?: 'left' | 'right' | 'center';
};

/**
 * 바깥이 쥔 정렬 — 주면 표는 받은 순서 그대로 펴고(안에서 정렬하지 않는다) 머리 누름을 `onSort` 로 넘긴다.
 * 머리 값: `account` · `date` · `view` · `engagementRate` · 반응 열(`like` · `comment` · `saved` · `share` · `download`) · 덧붙인 열의 `sortField`.
 * `sortable` 에 없는 머리는 누를 수 없고 화살표도 서지 않는다.
 */
export type PostTableSortControl = {
  field: string | null;
  direction: 'asc' | 'desc';
  sortable: ReadonlySet<string>;
  onSort: (field: string) => void;
};

const NO_EXTRA_COLUMNS: readonly PostTableExtraColumn[] = [];

/** 묶음 합계 줄의 숫자 — 칸 자리는 표의 조회수 · E/R · 반응 지표 열과 같다. 값은 부르는 쪽이 센다 */
export type PostTableSummary = {
  view: number;
  /** % — 셀 수 없으면 `null`(「-」) */
  engagementRate: number | null;
  metrics: Partial<Record<'like' | 'comment' | 'saved' | 'share' | 'download', number>>;
  /** 덧붙인 열 키 → 합계 칸 내용 — 없는 열은 빈칸으로 합쳐진다 */
  extra?: Readonly<Record<string, React.ReactNode>>;
};

/**
 * 묶어 그리는 줄 — 주면 표는 `posts`·쪽 번호 대신 이 차례대로 「글 줄들 → 합계 줄」을 그린다.
 * 접힌 묶음은 합계 줄만 선다. 합계 줄의 왼쪽 끝 단추가 `onToggle` 이다.
 */
export type PostTableRowSection = {
  key: string;
  label: React.ReactNode;
  posts: readonly any[];
  summary: PostTableSummary;
  collapsed?: boolean;
  onToggle?: () => void;
};

/** 표 맨 아래 합계 줄 */
export type PostTableTotalRow = { label: React.ReactNode; summary: PostTableSummary };

/** Firestore { result, evidence } 또는 구 배열만 저장된 경우 모두 처리 */
function normalizeVideoCheckEntry(raw: any) {
  if (raw == null) return { result: [], evidence: null };
  if (Array.isArray(raw)) return { result: raw, evidence: null };
  const result = Array.isArray(raw?.result) ? raw.result : [];
  const ev = raw?.evidence;
  const evidence =
    ev && typeof ev === 'object' && !Array.isArray(ev) ? ev : null;
  return { result, evidence };
}

function formatEvidenceTimestamp(raw: any) {
  if (raw == null || raw === '') return '';
  try {
    if (typeof raw?.toDate === 'function') {
      const d = raw.toDate();
      if (!Number.isNaN(d.getTime())) return formatTimestamp(d.getTime());
    }
  } catch {
    // ignore
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) return formatTimestamp(raw.getTime());
  if (typeof raw === 'number') return formatTimestamp(raw);
  if (typeof raw === 'object' && typeof raw.seconds === 'number') {
    const ms = raw.seconds * 1000 + (typeof raw.nanoseconds === 'number' ? raw.nanoseconds / 1e6 : 0);
    return formatTimestamp(ms);
  }
  return String(raw);
}

function hasRenderableEvidence(evidence: any) {
  if (!evidence || typeof evidence !== 'object') return false;
  return (
    String(evidence.audioTranscript ?? '').trim() !== '' ||
    String(evidence.overlayTexts ?? '').trim() !== '' ||
    String(evidence.products ?? '').trim() !== '' ||
    evidence.lastCheckedAt != null ||
    String(evidence.lastCheckedByUserId ?? '').trim() !== '' ||
    String(evidence.lastStatus ?? '').trim() !== ''
  );
}

function getPostSearchFieldsLower(post: any) {
  return [
    post?.title || '',
    post?.content || '',
    post?.author || '',
    post?.account || '',
    post?.username || '',
  ].map((field) => String(field).toLowerCase());
}

/** AND가 OR보다 우선: (A AND B) OR (C AND D) */
function matchesBooleanSearchSegments(fieldsLower: readonly string[], segments: readonly any[] | null | undefined) {
  const segs = Array.isArray(segments) ? segments : [];
  const orGroups: string[][] = [];
  let andGroup: string[] = [];
  segs.forEach((seg) => {
    if (seg?.type === 'token') {
      const t = String(seg.value || '').trim().toLowerCase();
      if (t) andGroup.push(t);
      return;
    }
    if (seg?.type === 'op' && String(seg.value || '').toUpperCase() === 'OR') {
      if (andGroup.length > 0) orGroups.push(andGroup);
      andGroup = [];
    }
  });
  if (andGroup.length > 0) orGroups.push(andGroup);
  if (orGroups.length === 0) return true;
  const termMatches = (term: string) => fieldsLower.some((field) => field.includes(term));
  return orGroups.some((group) => group.every(termMatches));
}

function hasClientSearchSegments(segments: readonly any[] | null | undefined): segments is readonly any[] {
  return Array.isArray(segments) && segments.some((s: any) => s?.type === 'token' && String(s.value || '').trim());
}

/**
 * 게시물을 테이블 형태로 표시하는 컴포넌트
 *
 * ⚠️ 아래 `@param` 들은 반드시 `props.` 접두사를 붙인다. 빠지면 TypeScript 가
 *    **첫 인자(=props 객체) 자체의 타입**으로 읽어서 `posts: Array` 하나가 props 전체가 돼 버린다.
 *    실제 코드대로(전부 기본값이 있거나 없어도 되는 값) 적는다.
 *
 * ⚠️ `currentSort` 는 `any` 로 둔다 — 호출부가 넘기는 모양이 하나가 아니다.
 *
 * @param {Object} props
 * @param {readonly any[]} [props.posts] - 게시물 배열 (읽기만 한다 — 안에서 뒤집거나 밀어 넣지 않는다)
 * @param {Function} [props.onClickPost] - 게시물 클릭 핸들러
 * @param {boolean} [props.ownDetail] - false면 줄을 눌러도 이 표의 상세를 열지 않는다(부르는 쪽이 제 상세를 연다 · 기본 true)
 * @param {'post'|'media'} [props.detail] - 이 표가 여는 상세 — `media` 면 소재 상세(이전/다음은 표에 편 줄 순서), `post` 면 글 상세. ⚠️ `post` 는 은퇴할 화면(대시보드·주간 리포트)만 쓴다
 * @param {Function} [props.onRowsChange] - 지금 펼친 줄(필터·정렬·페이지를 거친 순서 그대로)이 바뀔 때마다 부른다
 * @param {string|string[]} [props.searchKeyword] - 검색 키워드(텍스트 하이라이트 + 키워드가 포함된 행 전체에 연한 primary 배경)
 * @param {string|string[]} [props.clientSearch] - 클라이언트 검색어(필터/하이라이트용). 배열이면 다중 검색어
 * @param {string} [props.clientSearchOperator] - 다중 clientSearch 결합 방식 (기본 OR — 'AND' 가 아니면 전부 OR 로 떨어진다). clientSearchSegments가 있으면 무시
 * @param {readonly any[]|null} [props.clientSearchSegments] - token/AND/OR 세그먼트 검색식 (`type`·`value` 만 읽는다)
 * @param {Array<string>} [props.topicKeywords] - 토픽 키워드(필터/하이라이트용)
 * @param {any} [props.currentSort] - 바깥에서 강제하는 정렬 상태
 * @param {Function} [props.onFilteredCountChange] - 필터링된 게시물 개수 변경 시 콜백
 * @param {number} [props.page] - 현재 페이지
 * @param {number} [props.perPage] - 페이지당 항목 수
 * @param {string} [props.defaultSortField] - 기본 정렬 필드 ('account', 'date', 'engagement', 'engagementRate')
 * @param {string} [props.defaultSortDirection] - 기본 정렬 방향 ('asc' or 'desc')
 * @param {boolean} [props.disableSort] - true면 테이블 헤더 정렬 비활성화, 전달된 posts 순서 유지
 * @param {boolean} [props.disableRowKeywordHighlight] - true면 키워드 행 배경 강조를 끈다
 * @param {boolean} [props.hideCountry] - true면 국가 칼럼 숨김
 * @param {boolean} [props.hideChannel] - true면 채널 칼럼 숨김(계정 얼굴 배지가 채널을 보일 때)
 * @param {boolean} [props.dense] - true면 칸 좌우 여백을 줄인다(열이 많아 표가 넓을 때)
 * @param {ReadonlySet<string>} [props.hiddenMetricFields] - 숨길 반응 지표 열(like·comment·saved·share·download). 값·E/R 계산은 그대로다
 * @param {boolean} [props.enableImageBookmark] - true면 북마크 추가 컬럼 표시 (PostCard와 동일)
 * @param {boolean} [props.enableCampaignAdd] - true면 캠페인 추가 컬럼 표시
 * @param {boolean} [props.enableVideoCheckColumn] - true면 영상 검수 칼럼 표시
 * @param {Array} [props.extraColumns] - 부르는 쪽이 그리는 열 — `{ key, label, title?, minWidth?, render(post) }` · 칸 누름은 줄로 번지지 않는다
 * @param {'end'|'afterDate'|'afterView'|'afterReactions'|'beforeMemo'} [props.extraColumnsAt] - 그 열이 서는 자리 — `end`(기본 · 메모·영상 검수 뒤) · `afterReactions`(반응 열 바로 뒤 · 일차별 열 앞) · `beforeMemo`(반응·일차별 열 뒤 · 메모 앞) · `afterDate`(날짜 바로 뒤 · 지표 열 앞) · `afterView`(조회수 바로 뒤 · E/R 앞). 열마다 `at` 으로 바꿀 수 있다
 * @param {Array} [props.rowSections] - 묶어 그리는 줄(`PostTableRowSection`) — 주면 묶음마다 머리(소계) 줄 + 글 줄, 쪽 번호·안의 검색 거르기 없이 받은 그대로
 * @param {Object} [props.totalRow] - 표 맨 아래 합계 줄(`PostTableTotalRow`)
 * @param {Object} [props.sortControl] - 바깥이 쥔 정렬(`PostTableSortControl`) — 주면 받은 순서 그대로 펴고 머리 누름을 넘긴다
 * @param {string} [props.dateLabel] - 날짜 열 머리 이름(기본 「날짜」)
 * @param {Function} [props.dateText] - 날짜 칸 글자 — (post) => string(기본은 게시 시각 「YYYY-MM-DD HH:mm」)
 * @param {Function} [props.engagementRateOf] - E/R 칸의 값(%) — (post) => number|null(기본은 표의 좋아요·댓글·조회수로 낸 값 · null 이면 「-」)
 * @param {Function} [props.accountAvatarOf] - 계정 칸 앞 얼굴 — (post) => 주소|null(`AccountAvatar` · 없으면 채널 아이콘). 안 주면 얼굴 없이 글자만
 * @param {boolean} [props.enableMemoColumn] - true면 다운로드 오른쪽에 메모 칼럼(PostMemoProvider 가 상태를 쥔다)
 * @param {number} [props.memoLines] - 메모 칸에 보이는 줄 수(기본 3 · 넘치면 줄임표)
 * @param {boolean} [props.enableViewDayColumns] - true면 선택한 지표의 1/3/7/30일차 칼럼 표시
 * @param {string} [props.viewDayMetricKey] - enableViewDayColumns 시 표시할 metrics 필드 (viewCount, likeCount, …)
 * @param {boolean} [props.splitReactionColumns] - true면 반응 지표 다섯을 한 묶음(`reactions`) 대신 지표마다 한 열(`reaction:like` …)로 — 따로 켜고 끄고 옮긴다
 * @param {boolean} [props.viewDayColumnsPerMetric] - enableViewDayColumns 시 지표마다 한 열(`viewDays:{지표}` — 「{지표} 상세」 1·3·7·30일 묶음 · 처음엔 꺼짐 · `columnLayout` 으로 켠다) · 일차 머리 정렬 값은 `viewDay7:likeCount` 꼴. 없으면 `viewDayMetricKey` 한 묶음
 * @param {Object} [props.videoCheckByPostId] - postId → 검수 결과
 * @param {boolean} [props.videoCheckLoading] - 검수 결과 로딩 여부
 * @param {boolean} [props.isBookmark] - 북마크 화면에서 쓰는지
 * @param {Set<any>} [props.selectedPostIds] - 선택된 게시물 id
 * @param {Function} [props.onToggleSelect] - (post) => void
 * @param {boolean} [props.enablePostSelection] - true면 행 선택 체크박스 (캠페인 제외 등)
 * @param {Set<string>} [props.selectedPostKeys] - 선택된 행 키 (id 또는 link:…)
 * @param {Function} [props.onTogglePostSelection] - (post) => void
 * @param {Function} [props.getPostSelectionKey] - (post) => string
 * @param {Set<string>} [props.excludedKeysSet] - 제외된 컨텐츠 키 (행 표시용)
 * @param {boolean} [props.detailUsesMetricDetailSort] - 커스텀 서치 등: 상세 드로어에서 metricsSort 반영
 * @param {boolean} [props.ignoreMetricSortFields] - 캠페인 등: 테이블 메트릭·정렬에서 metricsSort·metricScoreSort 미사용
 * @param {Object} [props.columnLayout] - 워크스페이스 열 배치(`PostTableColumnLayout` — 열 id 차례 · 숨김). 없으면 기본 차례 그대로. 열 id 는 `postTableColumnCatalog`

 */
export default function PostTable({
  posts = [],
  onClickPost,
  ownDetail = true,
  detail = 'post',
  onRowsChange,
  searchKeyword = '',
  clientSearch = '',
  clientSearchOperator = 'OR',
  clientSearchSegments = null,
  topicKeywords = [],
  currentSort,
  onFilteredCountChange,
  page = 1,
  perPage = 40,
  defaultSortField = 'view',
  defaultSortDirection = 'desc',
  disableSort = false,
  disableRowKeywordHighlight = false,
  hideCountry = false,
  hideChannel = false,
  dense = false,
  hiddenMetricFields = NO_HIDDEN_METRIC_FIELDS,
  enableImageBookmark = true,
  renderRowAction,
  enableCampaignAdd = false,
  enableVideoCheckColumn = false,
  extraColumns = NO_EXTRA_COLUMNS,
  extraColumnsAt = 'end',
  sortControl,
  rowSections,
  totalRow,
  dateLabel = '날짜',
  dateText,
  engagementRateOf,
  accountAvatarOf,
  enableMemoColumn = false,
  memoLines,
  /** true면 선택한 지표의 1/3/7/30일차 칼럼 표시 */
  enableViewDayColumns = false,
  /** enableViewDayColumns 시 표시할 metrics 필드 (viewCount, likeCount, …) */
  viewDayMetricKey = 'viewCount',
  viewDayColumnsPerMetric = false,
  splitReactionColumns = false,
  videoCheckByPostId = {},
  videoCheckLoading = false,
  isBookmark = false,
  selectedPostIds = new Set(),
  onToggleSelect,
  enablePostSelection = false,
  selectedPostKeys = new Set(),
  onTogglePostSelection,
  getPostSelectionKey,
  excludedKeysSet = new Set(),
  /** 커스텀 서치 등: 상세 드로어에서 metricsSort 반영 */
  detailUsesMetricDetailSort = false,
  /** 캠페인 등: 테이블 메트릭·정렬에서 metricsSort·metricScoreSort 미사용 */
  ignoreMetricSortFields = false,
  columnLayout,
}: {
  posts?: readonly any[]
  onClickPost?: (post: any) => void
  ownDetail?: boolean
  detail?: 'post' | 'media'
  onRowsChange?: (posts: readonly any[]) => void
  searchKeyword?: string | readonly string[]
  clientSearch?: string | readonly string[]
  clientSearchOperator?: string
  clientSearchSegments?: readonly any[] | null
  topicKeywords?: readonly string[]
  currentSort?: any
  onFilteredCountChange?: (count: number) => void
  page?: number
  perPage?: number
  defaultSortField?: string
  defaultSortDirection?: string
  disableSort?: boolean
  disableRowKeywordHighlight?: boolean
  hideCountry?: boolean
  hideChannel?: boolean
  dense?: boolean
  hiddenMetricFields?: ReadonlySet<string>
  enableImageBookmark?: boolean
  /**
   * 줄마다 **북마크 옆에 세울 아이콘 한 칸** — 글 하나를 받아 그린다.
   * 🔑 부르는 화면이 제 단추를 표 «밖»에 두거나 아예 빠뜨리지 않게 연 자리다(담기 같은 것).
   * ⚠️ 돌려주는 것은 **아이콘 한 칸**이다 — 글자 단추를 넣으면 칸 너비가 무너진다.
   */
  renderRowAction?: (post: any) => React.ReactNode
  enableCampaignAdd?: boolean
  enableVideoCheckColumn?: boolean
  extraColumns?: readonly PostTableExtraColumn[]
  extraColumnsAt?: PostTableColumnAt
  sortControl?: PostTableSortControl
  rowSections?: readonly PostTableRowSection[]
  totalRow?: PostTableTotalRow
  dateLabel?: string
  dateText?: (post: any) => string
  engagementRateOf?: (post: any) => number | null
  accountAvatarOf?: (post: any) => string | null
  enableMemoColumn?: boolean
  memoLines?: number
  enableViewDayColumns?: boolean
  viewDayMetricKey?: string
  viewDayColumnsPerMetric?: boolean
  splitReactionColumns?: boolean
  videoCheckByPostId?: Record<string, any>
  videoCheckLoading?: boolean
  isBookmark?: boolean
  selectedPostIds?: ReadonlySet<any>
  onToggleSelect?: (post: any) => void
  enablePostSelection?: boolean
  selectedPostKeys?: ReadonlySet<string>
  onTogglePostSelection?: (post: any) => void
  getPostSelectionKey?: (post: any) => string
  excludedKeysSet?: ReadonlySet<string>
  detailUsesMetricDetailSort?: boolean
  ignoreMetricSortFields?: boolean
  columnLayout?: PostTableColumnLayout | null
}) {
  const metricsByScoreOpts = useMemo(
    () => (ignoreMetricSortFields ? { useMetricDetailSort: false } : {}),
    [ignoreMetricSortFields],
  );
  const popularityScoreOpts = useMemo(
    () => (ignoreMetricSortFields ? { useMetricScoreSort: false } : {}),
    [ignoreMetricSortFields],
  );
  const dayDetailMetricKey = useMemo(() => {
    const key = String(viewDayMetricKey || 'viewCount');
    return METRIC_DAY_DETAIL_OPTIONS.some((o) => o.value === key) ? key : 'viewCount';
  }, [viewDayMetricKey]);
  /** 일차 열 id → 그 묶음(읽는 지표 · 머리 이름 · 머리 정렬 값에 지표를 붙이나) — 한 줄짜리 `viewDays` 는 `viewDayMetricKey` 를 읽는다 */
  const dayGroupOf = useCallback((id: string): { key: string; label: string; perMetric: boolean } | null => {
    const metric = postTableDayColumnMetric(id);
    if (metric === null) return null;
    const key = metric || dayDetailMetricKey;
    const opt = METRIC_DAY_DETAIL_OPTIONS.find((o) => o.value === key);
    if (!opt) return metric ? null : { key, label: '조회수', perMetric: false };
    return { key: opt.value, label: opt.label, perMetric: Boolean(metric) };
  }, [dayDetailMetricKey]);

  const [selectedPost, setSelectedPost] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [isVideoCheckDialogOpen, setIsVideoCheckDialogOpen] = useState(false);
  const [videoCheckDialogResult, setVideoCheckDialogResult] = useState<any[]>([]);
  const [videoCheckDialogEvidence, setVideoCheckDialogEvidence] = useState<any>(null);
  const [, setVideoCheckDialogPostId] = useState('');
  const [sortField, setSortField] = useState(defaultSortField); // 'account', 'date', 'engagement', 'engagementRate'
  const [sortDirection, setSortDirection] = useState(defaultSortDirection); // 'asc' or 'desc'

  const visibleMetricFields = useMemo(
    () => METRIC_COLUMNS.map(({ field }) => field).filter((field) => !hiddenMetricFields.has(field)),
    [hiddenMetricFields]
  );

  const resolveSortStateFromCurrentSort = useCallback(
    (sortType: any) => {
      const v = String(sortType ?? '').trim()
      if (!v) return { sortField: defaultSortField, sortDirection: defaultSortDirection }

      if (v === SORT_TYPES.LATEST) return { sortField: 'date', sortDirection: 'desc' }
      if (v === SORT_TYPES.POPULAR) return { sortField: 'engagement', sortDirection: 'desc' }
      if (v === SORT_TYPES.COMMENT) return { sortField: 'comment', sortDirection: 'desc' }
      if (v === SORT_TYPES.LIKE) return { sortField: 'like', sortDirection: 'desc' }
      if (v === SORT_TYPES.VIEW) return { sortField: 'view', sortDirection: 'desc' }
      if (v === SORT_TYPES.ER) return { sortField: 'engagementRate', sortDirection: 'desc' }

      return { sortField: defaultSortField, sortDirection: defaultSortDirection }
    },
    [defaultSortDirection, defaultSortField]
  )

  useEffect(() => {
    if (!currentSort) return
    const next = resolveSortStateFromCurrentSort(currentSort)
    setSortField(next.sortField)
    setSortDirection(next.sortDirection)
  }, [currentSort, resolveSortStateFromCurrentSort])

  useEffect(() => {
    if (currentSort) return
    setSortField(defaultSortField)
    setSortDirection(defaultSortDirection)
  }, [currentSort, defaultSortDirection, defaultSortField])

  const handleRowClick = (post: any) => {
    if (ownDetail) {
      setSelectedPost(post);
      setIsDetailOpen(true);
    }
    if (typeof onClickPost === 'function') {
      onClickPost(post);
    }
  };

  const handleDetailClose = () => {
    setIsDetailOpen(false);
    setSelectedPost(null);
  };

  const handleOpenVideoCheckDialog = useCallback(({ postId, result, evidence }: { postId?: any; result?: any; evidence?: any }) => {
    setVideoCheckDialogPostId(String(postId || ''));
    setVideoCheckDialogResult(Array.isArray(result) ? result : []);
    setVideoCheckDialogEvidence(
      evidence && typeof evidence === 'object' && !Array.isArray(evidence) ? evidence : null
    );
    setIsVideoCheckDialogOpen(true);
  }, []);

  const handleCloseVideoCheckDialog = useCallback(() => {
    setIsVideoCheckDialogOpen(false);
    setVideoCheckDialogPostId('');
    setVideoCheckDialogResult([]);
    setVideoCheckDialogEvidence(null);
  }, []);

  const resolvePostSelectionKey = useCallback(
    (post: any) => {
      if (typeof getPostSelectionKey === 'function') return getPostSelectionKey(post);
      return String(post?.id || '').trim();
    },
    [getPostSelectionKey]
  );

  const showBookmarkCheckbox = Boolean(isBookmark && onToggleSelect);
  const showExclusionCheckbox = Boolean(enablePostSelection && onTogglePostSelection);


  // 검색 키워드를 문자열로 변환 (배열인 경우 쉼표로 join)
  const normalizeSearchKeyword = useCallback((kw: any) => {
    if (!kw) return '';
    if (Array.isArray(kw)) {
      return kw.filter(Boolean).join(', ');
    }
    return String(kw);
  }, []);

  const clientSearchTerms = useMemo(() => {
    if (hasClientSearchSegments(clientSearchSegments)) {
      return clientSearchSegments
        .filter((s: any) => s?.type === 'token')
        .map((s: any) => String(s.value || '').trim().toLowerCase())
        .filter(Boolean);
    }
    if (Array.isArray(clientSearch)) {
      return clientSearch.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean);
    }
    const s = String(clientSearch || '').trim().toLowerCase();
    if (!s) return [];
    // 쉼표 구분 다중 키워드도 terms로 취급
    const parts = s.split(',').map((t) => t.trim()).filter(Boolean);
    return parts.length > 0 ? parts : [s];
  }, [clientSearch, clientSearchSegments]);

  const clientSearchJoined = useMemo(
    () => clientSearchTerms.join(', '),
    [clientSearchTerms]
  );

  const isAndSearch = String(clientSearchOperator || 'OR').toUpperCase() === 'AND';
  const useSegmentSearch = hasClientSearchSegments(clientSearchSegments);

  const searchKeywordTerms = useMemo(() => {
    const raw = normalizeSearchKeyword(searchKeyword);
    return String(raw || '')
      .split(',')
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
  }, [searchKeyword, normalizeSearchKeyword]);

  /** 행에 표시되는 텍스트 중 검색 조건에 맞으면 true (행 전체 배경용) */
  const isPostRowMatchingKeyword = useCallback(
    (parts: any) => {
      const joined = parts
        .filter((p: any) => p != null && String(p).trim() !== '')
        .map((p: any) => removeHtmlTags(String(p)).toLowerCase())
        .join(' ');
      if (useSegmentSearch) {
        return matchesBooleanSearchSegments([joined], clientSearchSegments);
      }
      const terms = searchKeywordTerms.length > 0 ? searchKeywordTerms : clientSearchTerms;
      if (terms.length === 0) return false;
      return isAndSearch
        ? terms.every((term) => joined.includes(term))
        : terms.some((term) => joined.includes(term));
    },
    [useSegmentSearch, clientSearchSegments, searchKeywordTerms, clientSearchTerms, isAndSearch]
  );

  // 텍스트 하이라이트 함수
  const highlightText = useCallback((text: any, post?: any) => {
    const normalizedSearchKw = normalizeSearchKeyword(searchKeyword);
    const sentimentPositive = Array.isArray(post?.sentimentEvidencePositive) ? post.sentimentEvidencePositive : [];
    const sentimentNegative = readNegativeEvidence(post);


    return highlightTextWithHtmlRemoval(
      text,
      normalizedSearchKw,
      topicKeywords as string[],
      clientSearchJoined,
      sentimentPositive,
      sentimentNegative
    );
  }, [searchKeyword, topicKeywords, clientSearchJoined, normalizeSearchKeyword]);

  /**
   * 🔑 신원(계정명·작성자명)은 **마크업 없이** 그린다 — `dangerouslySetInnerHTML` 을 쓰지 않는다.
   * 그 값에 태그가 필요한 이유가 없는데 HTML 통로를 열어 두면 «글쓴이가 고른 문자열»이
   * 태그가 된다. React 가 텍스트를 늘 escape 하므로 여기서는 태그가 생길 수 없다.
   */
  const renderIdentity = (text: any, post?: any, prefix = '') => (
    <HighlightedText
      text={text}
      prefix={prefix}
      searchKeyword={normalizeSearchKeyword(searchKeyword)}
      topicKeywords={topicKeywords as string[]}
      clientSearch={clientSearchJoined}
      sentimentPositive={Array.isArray(post?.sentimentEvidencePositive) ? post.sentimentEvidencePositive : []}
      sentimentNegative={readNegativeEvidence(post)}
    />
  );





  const normalizeTimestampMs = normalizePostTimestampMs;

  // 시간 포맷팅 (정규화된 타임스탬프 기반)
  const formatTime = (post: any) => {
    const raw = post?.timestamp ?? post?.postDate ?? post?.date;
    const ms = normalizeTimestampMs(raw);
    if (ms === null) return '';
    return formatTimestamp(ms);
  };

  const getLatestMetrics = getLatestDateMetrics;

  // 메트릭 가져오기 (포맷팅된 문자열)
  const getMetrics = (post: any) => {
    const latestMetrics = getLatestMetrics(post);
    const fallbackMetrics = getMetricsByScoreTime(post, metricsByScoreOpts);

    return {
      like: formatNumber(latestMetrics?.likeCount ?? fallbackMetrics?.likeCount ?? post?.likeCount ?? post?.likes ?? 0),
      comment: formatNumber(latestMetrics?.commentCount ?? fallbackMetrics?.commentCount ?? post?.commentCount ?? post?.comments ?? 0),
      view: formatNumber(latestMetrics?.viewCount ?? fallbackMetrics?.viewCount ?? post?.viewCount ?? post?.views ?? post?.impressions ?? 0),
      saved: formatNumber(latestMetrics?.savedCount ?? fallbackMetrics?.savedCount ?? post?.savedCount ?? 0),
      share: formatNumber(latestMetrics?.shareCount ?? fallbackMetrics?.shareCount ?? post?.shareCount ?? 0),
      download: formatNumber(latestMetrics?.downloadCount ?? fallbackMetrics?.downloadCount ?? post?.downloadCount ?? 0),
    };
  };

  // 원본 메트릭 값 가져오기 (숫자) - '-', null 등은 0으로 치환
  const getRawMetrics = useCallback((post: any) => readPostTableRawMetrics(post, metricsByScoreOpts), [metricsByScoreOpts]);

  // 반응률(%) — 정렬용 · 조회가 0 이하면 null(정렬 시 가장 뒤로) · 셈은 `engagementRatePct` 하나다
  const calculateEngagementRateValue = useCallback((post: any) => {
    const { like, comment, view } = getRawMetrics(post);
    return engagementRatePct(like, comment, view);
  }, [getRawMetrics]);

  // Engagement Rate 포맷팅된 문자열 반환 (표시용)
  const calculateEngagementRate = (post: any) => {
    const rate = engagementRateOf ? engagementRateOf(post) : calculateEngagementRateValue(post);

    // 조회가 0 이하인 경우 "-" 반환
    if (rate === null) {
      return '-';
    }

    // 소수점 둘째 자리까지 표시
    return `${rate.toFixed(2)}%`;
  };

  // 정렬 핸들러 (disableSort 시 사용 안 함)
  const handleSort = (field: any) => {
    if (sortControl) {
      if (sortControl.sortable.has(field)) sortControl.onSort(field);
      return;
    }
    if (disableSort) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // 검색어/토픽키워드로 필터링된 게시물
  const filteredPosts = useMemo(() => {
    let result = posts;

    if (useSegmentSearch) {
      result = result.filter((post) =>
        matchesBooleanSearchSegments(getPostSearchFieldsLower(post), clientSearchSegments)
      );
    } else if (clientSearchTerms.length > 0) {
      result = result.filter((post) => {
        const fields = getPostSearchFieldsLower(post);
        const termMatches = (term: string) => fields.some((field: string) => field.includes(term));
        return isAndSearch
          ? clientSearchTerms.every(termMatches)
          : clientSearchTerms.some(termMatches);
      });
    }

    // topicKeywords로 추가 필터링 (있을 때만)
    if (Array.isArray(topicKeywords) && topicKeywords.length > 0) {
      result = result.filter((post) => {
        const fields = [
          post?.title || '',
          post?.content || '',
          post?.author || '',
          post?.account || '',
        ].map((field) => String(field).toLowerCase());
        return topicKeywords.some((keyword) =>
          fields.some((field) => field.includes(String(keyword || '').toLowerCase()))
        );
      });
    }

    return result;
  }, [posts, useSegmentSearch, clientSearchSegments, clientSearchTerms, isAndSearch, topicKeywords]);

  // 필터링된 개수를 부모에 전달 (페이지네이션 등에 사용)
  useEffect(() => {
    if (typeof onFilteredCountChange === 'function') {
      onFilteredCountChange(filteredPosts.length);
    }
  }, [filteredPosts.length, onFilteredCountChange]);

  // 정렬된 게시물 (disableSort · 바깥 정렬이면 전달된 순서 유지)
  const sortedPosts = useMemo(() => {
    if (disableSort || sortControl || !sortField) return filteredPosts;
    return sortPostsForTable(filteredPosts, sortField, sortDirection === 'asc' ? 'asc' : 'desc', {
      metricsByScoreOpts,
      popularityScoreOpts,
      dayMetricKey: dayDetailMetricKey,
    });
  }, [disableSort, sortControl, filteredPosts, sortField, sortDirection, metricsByScoreOpts, popularityScoreOpts, dayDetailMetricKey]);

  // 페이지네이션된 게시물
  const pagedPosts = useMemo(() => {
    if (rowSections) return rowSections.flatMap((section) => (section.collapsed ? [] : [...section.posts]));
    const start = (page - 1) * perPage;
    const end = start + perPage;
    return sortedPosts.slice(start, end);
  }, [rowSections, sortedPosts, page, perPage]);

  useEffect(() => {
    onRowsChange?.(pagedPosts);
  }, [pagedPosts, onRowsChange]);

  /** 이 머리를 눌러 정렬할 수 있나 — 바깥 정렬이면 그 `sortable`, 아니면 `disableSort` 가 아닐 때 전부 */
  function canSort(field: string): boolean {
    return sortControl ? sortControl.sortable.has(field) : !disableSort;
  }
  const sortableHeadSx = (field: string, hover = 'grey.50') =>
    (canSort(field) ? { cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: hover } } : {});

  // 정렬 아이콘 렌더링 (disableSort면 미표시)
  const renderSortIcon = (field: any) => {
    if (!canSort(field)) return null;
    const activeField = sortControl ? sortControl.field : sortField;
    const direction = sortControl ? sortControl.direction : sortDirection;
    if (activeField !== field) {
      return (
        <Iconify
          icon="solar:alt-arrow-up-linear"
          width={16}
          sx={{ color: 'grey.400', opacity: 0.5 }}
        />
      );
    }
    return (
      <Iconify
        icon={direction === 'asc' ? 'solar:alt-arrow-up-linear' : 'solar:alt-arrow-down-linear'}
        width={16}
        sx={{ color: 'primary.main' }}
      />
    );
  };

  const resolveVideoCheckKey = useCallback((post: any) => {
    const candidates = [
      post?.postId,
      post?.id,
      post?.contentId,
      post?.contentKey,
      post?.content_key,
      post?.post_id,
    ];
    const key = candidates.find((v) => String(v ?? '').trim().length > 0);
    return String(key ?? '').trim();
  }, []);

  const resolveVideoCheckData = useCallback((post: any) => {
    const baseKey = resolveVideoCheckKey(post);
    if (!baseKey) return normalizeVideoCheckEntry(null);

    const direct = videoCheckByPostId?.[baseKey];
    if (direct) return normalizeVideoCheckEntry(direct);

    const source = String(post?.source || post?.platform || '').trim().toLowerCase();
    if (source) {
      const withSourceKey = `${source}-${baseKey}`;
      const withSource = videoCheckByPostId?.[withSourceKey];
      if (withSource) return normalizeVideoCheckEntry(withSource);
    }

    if (baseKey.includes('-')) {
      const withoutPrefix = baseKey.split('-').slice(1).join('-');
      const stripped = videoCheckByPostId?.[withoutPrefix];
      if (stripped) return normalizeVideoCheckEntry(stripped);
    }

    return normalizeVideoCheckEntry(null);
  }, [resolveVideoCheckKey, videoCheckByPostId]);

  const renderPassedDot = useCallback((passed: any, index: number) => {
    const isPassed = Boolean(passed);
    return (
      <Box
        key={`video-check-dot-${index}`}
        sx={{
          width: 10,
          height: 10,
          borderRadius: '50%',
          bgcolor: isPassed ? 'success.main' : 'error.main',
          boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)',
        }}
      />
    );
  }, []);

  const renderEvidenceTimecodes = useCallback((timecodes: any) => {
    if (!Array.isArray(timecodes) || timecodes.length === 0) return null;
    const normalized = timecodes.map((t) => String(t ?? '').trim()).filter(Boolean);
    if (normalized.length === 0) return null;
    return normalized.join(', ');
  }, []);

  /** 표에 서는 열(기본 차례)과 이번에 펼 열 — `columnLayout` 이 없으면 기본 차례 그대로 */
  const columnCatalog = postTableColumnCatalog({
    hideChannel,
    hideCountry,
    enableViewDayColumns,
    viewDayColumnsPerMetric,
    splitReactionColumns,
    hiddenMetricFields,
    enableMemoColumn,
    enableVideoCheckColumn,
    extraColumns,
    extraColumnsAt,
    dateLabel,
  });
  // 지표마다 선 일차 열 · `defaultHidden` 을 단 덧붙인 열은 처음엔 꺼져 있다 — 한 줄짜리 `viewDays` 는 켜면 배치 없이도 선다
  const defaultHiddenIds = postTableDefaultHiddenIds(columnCatalog).filter((id) => viewDayColumnsPerMetric || id !== 'viewDays');
  const visibleColumnIds = resolvePostTableColumns(
    columnCatalog.map((c) => c.id),
    columnLayout,
    defaultHiddenIds.length > 0 ? defaultHiddenIds : NO_COLUMN_IDS,
  );
  /** 1·3·7·30일 열이 서면 머리가 두 줄이다 */
  const showDayHeadRow = visibleColumnIds.some((id) => dayGroupOf(id));
  const headRowSpan = showDayHeadRow ? 2 : 1;
  const extraById = new Map(extraColumns.map((col) => [postTableExtraColumnId(col.key), col]));
  /** 열 하나가 차지하는 칸 수 — 합계 줄·빈 줄의 colSpan */
  const columnSpan = (id: string) => (dayGroupOf(id) ? VIEW_DAY_COLUMNS.length : id === 'reactions' ? visibleMetricFields.length : 1);
  /** 반응 지표 한 칸짜리 열(`reaction:like`)의 지표 — 표에 서는 지표가 아니면 `null` */
  const reactionFieldOf = (id: string): string | null => {
    const field = postTableReactionColumnField(id);
    return field && visibleMetricFields.includes(field as (typeof visibleMetricFields)[number]) ? field : null;
  };
  /** 일차 묶음이 바로 앞 열도 일차 묶음일 때 그 첫 칸 왼쪽의 세로선 */
  const dayGroupEdgeSx = (id: string, dayIndex: number) => {
    const at = visibleColumnIds.indexOf(id);
    return dayIndex === 0 && at > 0 && dayGroupOf(visibleColumnIds[at - 1]) ? DAY_GROUP_EDGE_SX : null;
  };
  /** 「데이터 없음」 줄의 colSpan — 배치가 없으면 고정 셈(한 칸 넉넉하다) 그대로 */
  const emptyColSpan = columnLayout || viewDayColumnsPerMetric
    ? 1 + visibleColumnIds.reduce((sum, id) => sum + columnSpan(id), 0)
    : 14 - (METRIC_COLUMNS.length - visibleMetricFields.length) + (hideCountry ? 0 : 1) - (hideChannel ? 1 : 0) + (enableMemoColumn ? 1 : 0) + (enableVideoCheckColumn ? 1 : 0) + extraColumns.length + (enableViewDayColumns ? VIEW_DAY_COLUMNS.length : 0);

  /** 부르는 쪽이 그리는 열 — 머리 · 칸 */
  const extraHead = (col: PostTableExtraColumn) => {
    const sortable = !!col.sortField && canSort(col.sortField);
    return (
      <TableCell
        key={`extra-head-${col.key}`}
        rowSpan={headRowSpan}
        title={col.title}
        onClick={sortable ? () => handleSort(col.sortField) : undefined}
        align={col.align}
        sx={{ typography: 'uiStrong', py: 1.5, minWidth: col.minWidth ?? 56, verticalAlign: 'middle', whiteSpace: 'nowrap', ...(sortable ? sortableHeadSx(col.sortField as string) : {}) }}
      >
        {sortable ? (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <span>{col.label}</span>
            {renderSortIcon(col.sortField)}
          </Stack>
        ) : col.label}
      </TableCell>
    );
  };
  const extraCell = (post: any, col: PostTableExtraColumn) => (
    <TableCell key={`extra-${col.key}`} align={col.align} sx={{ minWidth: col.minWidth ?? 56 }} onClick={col.rowClick ? undefined : (e) => e.stopPropagation()}>
      {col.render(post)}
    </TableCell>
  );

  /** 열 id → 머리 첫 줄의 칸 */
  const headOf: Record<string, (fields?: readonly string[]) => React.ReactNode> = {
    channel: () => (
                <TableCell
                  rowSpan={headRowSpan}
                  sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: 60, verticalAlign: 'middle' }}
                >
                  채널
                </TableCell>
    ),
    account: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{
                  typography: 'uiStrong',
                  whiteSpace: 'nowrap',
                  py: 1.5,
                  minWidth: 100,
                  maxWidth: 200,
                  verticalAlign: 'middle',
                  ...sortableHeadSx('account'),
                }}
                onClick={() => handleSort('account')}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>계정</span>
                  {renderSortIcon('account')}
                </Stack>
              </TableCell>
    ),
    image: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: dense ? 76 : 100, verticalAlign: 'middle' }}
              >
                이미지
              </TableCell>
    ),
    content: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: dense ? 180 : 200, verticalAlign: 'middle' }}
              >
                제목/내용
              </TableCell>
    ),
    country: () => (
                <TableCell
                  rowSpan={headRowSpan}
                  sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: 80, verticalAlign: 'middle' }}
                >
                  국가
                </TableCell>
    ),
    date: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{
                  typography: 'uiStrong',
                  whiteSpace: 'nowrap',
                  py: 1.5,
                  minWidth: dense ? 92 : 100,
                  verticalAlign: 'middle',
                  ...sortableHeadSx('date'),
                }}
                onClick={() => handleSort('date')}
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>{dateLabel}</span>
                  {renderSortIcon('date')}
                </Stack>
              </TableCell>
    ),
    view: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{
                  typography: 'uiStrong',
                  whiteSpace: 'nowrap',
                  py: 1.5,
                  minWidth: dense ? 76 : 100,
                  verticalAlign: 'middle',
                  ...sortableHeadSx('view'),
                }}
                onClick={() => handleSort('view')}
                data-metric="view"
              >
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <span>조회수</span>
                  {renderSortIcon('view')}
                </Stack>
              </TableCell>
    ),
    er: () => (
              <TableCell
                rowSpan={headRowSpan}
                sx={{
                  typography: 'uiStrong',
                  whiteSpace: 'nowrap',
                  py: 1.5,
                  minWidth: dense ? 64 : 80,
                  verticalAlign: 'middle',
                  ...sortableHeadSx('engagementRate'),
                }}
                onClick={() => handleSort('engagementRate')}
                data-metric="er"
              >
                <Tooltip
                  title={`반응률(E/R) = ${ENGAGEMENT_RATE_FORMULA}`}
                  arrow
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>E/R</span>
                    {renderSortIcon('engagementRate')}
                  </Stack>
                </Tooltip>
              </TableCell>
    ),
    reactions: (fields = visibleMetricFields) => METRIC_COLUMNS.filter(({ field }) => fields.includes(field)).map(({ field, label, icon }) => (
                <TableCell
                  key={field}
                  data-metric={field}
                  rowSpan={headRowSpan}
                  align="right"
                  sx={{
                    py: 1.5,
                    minWidth: dense ? 56 : 70,
                    verticalAlign: 'middle',
                    ...sortableHeadSx(field),
                  }}
                  onClick={() => handleSort(field)}
                >
                  <Tooltip title={label} arrow>
                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="flex-end">
                      <Box component="img" src={icon} alt={label} sx={{ width: 16, height: 16, opacity: 0.6 }} />
                      {renderSortIcon(field)}
                    </Stack>
                  </Tooltip>
                </TableCell>
              )),
    memo: () => (
                <TableCell
                  rowSpan={headRowSpan}
                  align="left"
                  sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: 260, verticalAlign: 'middle' }}
                >
                  메모
                </TableCell>
    ),
    videoCheck: () => (
                <TableCell
                  rowSpan={headRowSpan}
                  sx={{
                    typography: 'uiStrong',
                    whiteSpace: 'nowrap',
                    py: 1.5,
                    minWidth: 120,
                    verticalAlign: 'middle',
                  }}
                >
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <span>영상 평가</span>
                    {videoCheckLoading && <CachedIcon sx={{ typography: 'uiTitle', color: 'grey.500' }} />}
                  </Stack>
                </TableCell>
    ),
  };
  /** 일차 묶음의 머리 첫 줄 — 「{지표} 상세」 */
  const dayHead = (id: string, group: { label: string }) => (
    <TableCell
      colSpan={VIEW_DAY_COLUMNS.length}
      align="center"
      sx={{
        typography: 'uiStrong',
        whiteSpace: 'nowrap',
        py: 1,
        bgcolor: 'grey.100',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        ...dayGroupEdgeSx(id, 0),
      }}
    >
      {group.label} 상세
    </TableCell>
  );
  /** 일차 묶음의 칸 넷 — 게시 후 1·3·7·30일 시점 값 */
  const dayCells = (post: any, id: string, group: { key: string }) => VIEW_DAY_COLUMNS.map(({ day, field }, dayIndex) => {
    const dayValue = getMetricAtDay(post, day, group.key);
    const display = dayValue == null ? '-' : (dayValue ? formatNumber(dayValue) : '-');
    const edgeSx = dayGroupEdgeSx(id, dayIndex);
    return (
      <TableCell key={field} align="center" sx={edgeSx ?? undefined}>
        <Typography sx={{ typography: 'uiBody', color: display === '-' ? 'grey.400' : 'black', fontWeight: 'Medium' }}>
          {display}
        </Typography>
      </TableCell>
    );
  });
  const renderHeadCell = (id: string) => {
    const extra = extraById.get(id);
    const group = dayGroupOf(id);
    const field = reactionFieldOf(id);
    return <React.Fragment key={id}>{extra ? extraHead(extra) : group ? dayHead(id, group) : field ? headOf.reactions([field]) : headOf[id]?.()}</React.Fragment>;
  };

  /** 글 한 줄 — `rowNumber` 는 # 칸의 번호 */
  const renderPostRow = (post: any, idx: number, rowNumber: number, keyPrefix: string) => {
                const metrics = getMetrics(post);
                const platformIconPath = post?.source ? `/assets/icons/platforms/${String(post.source).toLowerCase()}.svg` : null;
                const marketName = post?.market ? countryLabel(post.market) : '';
                const titleOrContent = post?.title || post?.content || '';
                // 이미지 우선순위: imageS3Url > imageUrl > null
                const imageSrc = post?.imageS3Url || post?.imageUrl || null;

                const isRepost = Boolean(post?.isRepost);
                const isQuote = Boolean(post?.isQuote);
                const isComment = Boolean(post?.isComment);
                const isStory = Boolean(post?.isStory);
                const isRepostClone = isRepost && Boolean(post?.repostAuthor || post?.repostAccount || post?.repostOriginalPost);

                const selectionKey = resolvePostSelectionKey(post);
                const isExcludedFromCampaign = Boolean(
                  selectionKey && excludedKeysSet?.has?.(selectionKey)
                );

                const accountForMatch = isRepostClone
                  ? (post?.repostAccount || post?.account || post?.username || '')
                  : (post?.account || post?.username || '');
                const authorForMatch = isRepostClone
                  ? (post?.repostAuthor || post?.author || '')
                  : (post?.author || '');
                const accountCellSearchBlob = [authorForMatch, accountForMatch]
                  .filter((s) => String(s ?? '').trim().length > 0)
                  .join(' ');

                const rawForRow = getRawMetrics(post);
                const viewStrForMatch = rawForRow.view ? formatNumber(rawForRow.view) : '';
                const erStrForMatch = calculateEngagementRate(post);
                const hashtagRowStr = Array.isArray(post?.hashtags)
                  ? post.hashtags.map((h: any) => String(h ?? '').replace(/^#/, '')).join(' ')
                  : '';
                const metricPartsForMatch = visibleMetricFields.map((metricKey) => {
                  const rawVal = (rawForRow as any)[metricKey];
                  const display = isRepostClone || !rawVal ? '-' : (metrics as any)[metricKey];
                  return display === '-' ? '' : String(display);
                });

                const rowKeywordMatch = isPostRowMatchingKeyword([
                  post?.source,
                  accountCellSearchBlob,
                  removeHtmlTags(titleOrContent),
                  hashtagRowStr,
                  marketName,
                  formatTime(post),
                  viewStrForMatch,
                  erStrForMatch,
                  ...metricPartsForMatch,
                ]);

                const shouldHighlightRow = !disableRowKeywordHighlight && rowKeywordMatch;

                /** 열 id → 이 줄의 칸 — 차례는 `visibleColumnIds` 가 정한다 */
                const cellOf: Record<string, (fields?: readonly string[]) => React.ReactNode> = {
                  channel: () => (
                      <TableCell>
                        {platformIconPath ? (
                          <Box
                            component="img"
                            src={platformIconPath}
                            alt={post?.source}
                            sx={{ width: 24, height: 24 }}
                          />
                        ) : (
                          <Typography sx={{ typography: 'uiMeta', color: 'grey.500' }}>
                            {String(post?.source || '').toUpperCase()}
                          </Typography>
                        )}
                      </TableCell>
                  ),
                  account: () => (
                    <TableCell sx={{ minWidth: 100, maxWidth: 200 }}>
                      <AccountFaceFrame face={accountAvatarOf ? accountAvatarOf(post) : undefined} channel={post?.source}>
                      {(() => {
                        // 리포스트인 경우 repostAccount와 repostAuthor 사용
                        const account = isRepost
                          ? (post?.repostAccount || post?.account || post?.username || '')
                          : (post?.account || post?.username || '');
                        // 이름 자리의 플랫폼 오류 문구는 빈 이름으로 본다 — @계정만 선다(`displayAuthorName`)
                        const author = displayAuthorName(isRepost
                          ? (post?.repostAuthor || post?.author || '')
                          : (post?.author || ''));
                        const hasAccount = account.trim().length > 0;
                        const hasAuthor = author.trim().length > 0;

                        if (hasAccount && hasAuthor && account !== author) {
                          // 둘 다 있고 다른 경우: author / account 형식으로 표시
                          return (
                            <Stack>
                              <Typography
                                variant='caption'
                                sx={{ fontWeight: 600 }}
                              >
                                {renderIdentity(author, post)}
                              </Typography>
                              <Typography
                                variant='caption'
                              >
                                {renderIdentity(account, post, '@')}
                              </Typography>
                            </Stack>
                          );
                        } else if (hasAccount) {
                          // account만 있는 경우
                          return (
                            <Typography
                              variant='caption'
                              sx={{ fontWeight: 600 }}
                            >
                              {renderIdentity(account, post, '@')}
                            </Typography>
                          );
                        } else if (hasAuthor) {
                          // author만 있는 경우
                          return (
                            <Typography
                              variant='subtitle2'
                            >
                              {renderIdentity(author, post)}
                            </Typography>
                          );
                        } else {
                          // 둘 다 없는 경우
                          return (
                            <Typography
                              variant='caption'
                              sx={{ fontWeight: 600 }}
                            >
                              알 수 없음
                            </Typography>
                          );
                        }
                      })()}
                      </AccountFaceFrame>
                    </TableCell>
                  ),
                  image: () => (
                    <TableCell>
                      {imageSrc && (
                        <Box
                          component="img"
                          src={imageSrc}
                          alt="post image"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                          sx={{
                            width: 60,
                            height: 60,
                            borderRadius: RADIUS.sm,
                            objectFit: 'cover',
                            bgcolor: 'grey.100',
                          }}
                        />
                      )}
                    </TableCell>
                  ),
                  content: () => (
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: 'wrap' }}>
                        {isExcludedFromCampaign && (
                          <Chip
                            label="캠페인 제외"
                            size="small"
                            color="default"
                            sx={{ height: 22, typography: 'uiLabel', flexShrink: 0 }}
                          />
                        )}
                        {/* 유형·광고 칩 — 칩 넷의 라벨·색·크기가 PostKindChips 의 `table`
                            변형과 같아 사본을 합쳤다(틱톡 광고 태그도 함께 온다).
                            「캠페인 제외」는 이 표에만 있는 칩이라 바깥에 남긴다 */}
                        <PostKindChips
                          post={post}
                          flags={{ isComment, isQuote, isRepost, isStory }}
                          variant="table"
                        />
                        <Tooltip title={removeHtmlTags(titleOrContent)} arrow>
                          <Typography
                            sx={{
                              typography: 'uiBody',
                              color: 'black',
                              lineHeight: 1.4,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              maxWidth: 300,
                            }}
                          >
                            <SafeHtml html={highlightText(removeHtmlTags(titleOrContent), post)} />
                          </Typography>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  ),
                  country: () => (
                      <TableCell>
                        {marketName ? (
                          <Chip
                            label={marketName}
                            size="small"
                            sx={{
                              height: 24,
                              typography: 'uiMeta',
                              bgcolor: 'grey.100',
                              color: 'grey.700',
                              fontWeight: 'Regular',
                            }}
                          />
                        ) : (
                          <Typography sx={{ typography: 'uiMeta', color: 'grey.400' }}>-</Typography>
                        )}
                      </TableCell>
                  ),
                  date: () => (
                    <TableCell>
                      <Typography sx={{ typography: 'uiMeta', color: 'black' }}>
                        {dateText ? dateText(post) : formatTime(post)}
                      </Typography>
                    </TableCell>
                  ),
                  view: () => (
                    <TableCell data-metric="view">
                      <Typography sx={{ typography: 'uiBody', color: getRawMetrics(post).view ? 'black' : 'grey.400', fontWeight: 'Medium' }}>
                        {getRawMetrics(post).view ? formatNumber(getRawMetrics(post).view) : '-'}
                      </Typography>
                    </TableCell>
                  ),
                  er: () => (
                    <TableCell data-metric="er">
                      <Typography sx={{ typography: 'uiBody', color: 'black', fontWeight: 'Medium' }}>
                        {calculateEngagementRate(post)}
                      </Typography>
                    </TableCell>
                  ),
                  reactions: (fields = visibleMetricFields) => fields.map((metricKey) => {
                      const rawVal = (getRawMetrics(post) as any)[metricKey];
                      // eslint-disable-next-line no-shadow
                      const isRepostClone = Boolean(post?.isRepost) && Boolean(post?.repostAuthor || post?.repostAccount || post?.repostOriginalPost);
                      const display = isRepostClone || !rawVal ? '-' : (metrics as any)[metricKey];
                      return (
                        <TableCell key={metricKey} data-metric={metricKey} align="right">
                          <Typography sx={{ typography: 'uiBody', color: display === '-' ? 'grey.400' : 'black', fontWeight: 'Medium' }}>
                            {display}
                          </Typography>
                        </TableCell>
                      );
                    }),
                  memo: () => (
                      <TableCell align="left" onClick={(e) => e.stopPropagation()}>
                        <PostMemoCell post={post} lines={memoLines} />
                      </TableCell>
                  ),
                  videoCheck: () => (
                      <TableCell sx={{ minWidth: 120 }} onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const key = resolveVideoCheckKey(post);
                          const { result: normalizedResult, evidence: rowEvidence } = resolveVideoCheckData(post);
                          const hasResult = normalizedResult.length > 0;
                          const canOpenDetail = hasResult || hasRenderableEvidence(rowEvidence);

                          if (!canOpenDetail) {
                            return (
                              <Typography sx={{ typography: 'uiBody', color: 'grey.400', fontWeight: 'Medium' }}>
                                -
                              </Typography>
                            );
                          }

                          return (
                            <Stack alignItems="center">
                              {hasResult ? (
                                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                                  {normalizedResult.map((item: any, index: number) => renderPassedDot(item?.a?.passed, index))}
                                </Stack>
                              ) : null}
                              <Button
                                size="small"
                                onClick={() => handleOpenVideoCheckDialog({
                                  postId: key,
                                  result: normalizedResult,
                                  evidence: rowEvidence,
                                })}
                                aria-label="상세 보기"
                                variant='soft'
                                sx={{ fontWeight: 'Medium', minHeight: 0, height: 24, mt: hasResult ? 1 : 0 }}
                              >
                                상세 보기
                              </Button>
                            </Stack>
                          );
                        })()}
                      </TableCell>
                  ),
                };

                return (
                  <TableRow
                    key={`${keyPrefix}-${idx}-${post?.id ?? post?.link ?? ''}`}
                    data-capture="row"
                    sx={{
                      cursor: 'pointer',
                      ...(isExcludedFromCampaign
                        ? { bgcolor: 'grey.100', opacity: 0.92 }
                        : shouldHighlightRow
                          ? { bgcolor: 'primary.lighter' }
                          : {}),
                      '&:hover': {
                        bgcolor: isExcludedFromCampaign
                          ? 'grey.100'
                          : shouldHighlightRow
                            ? 'primary.light'
                            : 'grey.50',
                      },
                    }}
                    onClick={() => handleRowClick(post)}
                  >
                    {/* 행번호 + 체크박스/북마크 (번호 아래 배치) */}
                    <TableCell
                      sx={{ width: 50, minWidth: 50, py: 0.5, verticalAlign: 'middle' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Stack direction="column" alignItems="center" spacing={0.25}>
                        <Typography sx={{ typography: 'uiBody', color: 'text.secondary', fontWeight: 'Medium' }}>
                          {rowNumber}
                        </Typography>
                        {(showBookmarkCheckbox || (showExclusionCheckbox && selectionKey)) && (
                          <Checkbox
                            size="small"
                            checked={
                              showBookmarkCheckbox
                                ? selectedPostIds.has(post?.id)
                                : selectedPostKeys.has(selectionKey)
                            }
                            onChange={() => {
                              // ⚠️ JSDoc 상 옵셔널이지만 이 갈래는 무가드 호출이 계약이다 — 동작 보존을 위해 단언
                              if (showBookmarkCheckbox) onToggleSelect!(post?.id);
                              else if (showExclusionCheckbox && selectionKey) onTogglePostSelection!(post);
                            }}
                            sx={{ p: 0.25 }}
                            aria-label={`${post?.title ?? '게시물'} 선택`}
                          />
                        )}
                        {(enableImageBookmark || enableCampaignAdd || renderRowAction)
                          && !isBookmark
                          && !showExclusionCheckbox && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              {renderRowAction?.(post)}
                              {enableImageBookmark && (
                                <PostCardBookmarkOverlay
                                  placement="postTable"
                                  post={post}
                                />
                              )}
                              {enableCampaignAdd && (
                                <PostCardCampaignOverlay
                                  placement="postTable"
                                  post={post}
                                />
                              )}
                            </Stack>
                          )}
                      </Stack>
                    </TableCell>

                    {visibleColumnIds.map((id) => {
                      const extra = extraById.get(id);
                      const group = dayGroupOf(id);
                      const field = reactionFieldOf(id);
                      return <React.Fragment key={id}>{extra ? extraCell(post, extra) : group ? dayCells(post, id, group) : field ? cellOf.reactions([field]) : cellOf[id]?.()}</React.Fragment>;
                    })}

                  </TableRow>
                );
  };

  /**
   * 합계 줄 — 칸 자리를 머리와 맞춘다. 숫자가 서는 열(조회수 · E/R · 반응)은 제 칸, 그 사이 숫자 없는 열들은 한 칸으로 합친다.
   * 접기 화살표와 이름(`label`)은 늘 줄 맨 앞(`#` 칸부터 첫 숫자 열 앞까지)에 선다 — 열 차례를 바꿔도 숫자는 제 머리 아래 선다.
   * 묶음 머리(`onToggle`)는 그 묶음의 소계를 겸한다 — 접으면 이 한 줄만 남는다. 총계 줄은 위에 굵은 선.
   */
  const renderSummaryRow = (
    key: string,
    label: React.ReactNode,
    summary: PostTableSummary,
    opts: { total?: boolean; collapsed?: boolean; onToggle?: () => void } = {},
  ) => {
    const cellSx = { typography: 'uiStrong', fontWeight: 700, color: 'text.primary' } as const;
    const summaryOf: Record<string, (fields?: readonly string[]) => React.ReactNode> = {
      view: () => <TableCell key="summary-view" data-metric="view" sx={cellSx}>{summary.view ? formatNumber(summary.view) : '-'}</TableCell>,
      er: () => <TableCell key="summary-er" data-metric="er" sx={cellSx}>{summary.engagementRate === null ? '-' : `${summary.engagementRate.toFixed(2)}%`}</TableCell>,
      reactions: (fields = visibleMetricFields) => fields.map((metricKey) => {
        const value = Number((summary.metrics as Record<string, number | undefined>)[metricKey] ?? 0);
        return (
          <TableCell key={`summary-${metricKey}`} data-metric={metricKey} align="right" sx={cellSx}>
            {value ? formatNumber(value) : '-'}
          </TableCell>
        );
      }),
    };
    // 이름 칸 = `#` 칸 + 첫 숫자 열 앞의 숫자 없는 열들 — 숫자 열이 맨 앞이면 `#` 칸 하나(글자가 안 꺾여 그 열이 넓어진다)
    /** 열 id → 합계 칸 — 반응 지표 한 칸짜리 열은 그 지표만 · 덧붙인 열은 `summary.extra` 에 그 키가 있을 때만 */
    const summaryRenderOf = (id: string) => {
      const extra = extraById.get(id);
      if (extra) {
        if (!summary.extra || !(extra.key in summary.extra)) return undefined;
        return () => <TableCell key={`summary-extra-${extra.key}`} align={extra.align} sx={cellSx}>{summary.extra?.[extra.key]}</TableCell>;
      }
      const field = reactionFieldOf(id);
      return field ? () => summaryOf.reactions([field]) : summaryOf[id];
    };
    const firstSummary = visibleColumnIds.findIndex((id) => summaryRenderOf(id));
    const leadIds = firstSummary < 0 ? visibleColumnIds : visibleColumnIds.slice(0, firstSummary);
    const leadSpan = leadIds.reduce((sum, id) => sum + columnSpan(id), 0);
    const cells: React.ReactNode[] = [];
    let run = 0;
    const flush = (at: string) => {
      if (run > 0) cells.push(<TableCell key={`summary-blank-${at}`} colSpan={run} />);
      run = 0;
    };
    visibleColumnIds.slice(leadIds.length).forEach((id) => {
      const render = summaryRenderOf(id);
      if (!render) {
        run += columnSpan(id);
        return;
      }
      flush(id);
      cells.push(render());
    });
    flush('tail');
    return (
      <TableRow
        key={`summary-${key}`}
        data-capture={opts.total ? 'total-row' : 'subtotal-row'}
        sx={{
          bgcolor: 'grey.100',
          '& > td': { borderBottom: '1px solid', borderColor: 'divider' },
          ...(opts.total ? { '& > td': { borderTop: 2, borderBottom: 0, borderColor: 'text.primary' } } : null),
        }}
      >
        <TableCell key="summary-label" colSpan={1 + leadSpan} sx={{ ...cellSx, minWidth: 50, py: 0.5, ...(leadSpan === 0 ? { whiteSpace: 'nowrap' } : null) }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            {opts.onToggle ? (
              <IconButton
                size="small"
                onClick={opts.onToggle}
                aria-label={opts.collapsed ? '묶음 펼치기' : '묶음 접기'}
                aria-expanded={!opts.collapsed}
              >
                <Iconify icon={opts.collapsed ? 'eva:arrow-ios-forward-fill' : 'eva:arrow-ios-downward-fill'} width={16} />
              </IconButton>
            ) : (
              <Box sx={{ width: 30, flexShrink: 0 }} />
            )}
            <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
          </Stack>
        </TableCell>
        {cells}
      </TableRow>
    );
  };

  /** 묶음마다 머리(소계) 줄 → 글 줄들 · 번호는 묶음 안에서 1부터 · 접으면 머리 줄만 */
  const renderSectionRows = (sections: readonly PostTableRowSection[]) => sections.flatMap((section) => [
    renderSummaryRow(section.key, section.label, section.summary, { collapsed: section.collapsed, onToggle: section.onToggle }),
    ...(section.collapsed ? [] : section.posts.map((post, idx) => renderPostRow(post, idx, idx + 1, `section-${section.key}`))),
  ]);

  return (
    <Box>
      <TableContainer
        component={Paper}
        data-capture="result"
        sx={{
          boxShadow: 'none',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: RADIUS.lg,
          overflow: 'auto',
        }}
      >
        <Table stickyHeader sx={dense ? DENSE_TABLE_SX : undefined}>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={headRowSpan}
                sx={{ typography: 'uiStrong', py: 1.5, whiteSpace: 'nowrap', minWidth: 50, width: 50, verticalAlign: 'middle' }}
              >
                #
              </TableCell>
              {visibleColumnIds.map(renderHeadCell)}
            </TableRow>
            {showDayHeadRow && (
              /* ⚠️ 테마는 마지막 줄의 칸 선을 지운다(`MuiTableRow` 의 `:last-of-type`) — 머리 둘째 줄이 마지막 줄이라 선을 되살려야 머리 밑선이 이어진다 */
              <TableRow sx={DAY_HEAD_ROW_SX}>
                {visibleColumnIds.flatMap((id) => {
                  const group = dayGroupOf(id);
                  return group ? [{ id, group }] : [];
                }).flatMap(({ id, group }) => VIEW_DAY_COLUMNS.map(({ day, label }, dayIndex) => {
                  const sortKey = dayMetricSortField(day, group.perMetric ? group.key : undefined);
                  return (
                    <TableCell
                      key={`${id}-${day}`}
                      align="center"
                      sx={{
                        typography: 'uiStrong',
                        whiteSpace: 'nowrap',
                        py: 1,
                        minWidth: 90,
                        bgcolor: 'grey.100',
                        top: 37,
                        ...dayGroupEdgeSx(id, dayIndex),
                        ...sortableHeadSx(sortKey, 'grey.200'),
                      }}
                      onClick={() => handleSort(sortKey)}
                    >
                      <Tooltip title={`게시 후 D+${day} 시점 ${group.label}`} arrow>
                        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                          <span>{label}</span>
                          {renderSortIcon(sortKey)}
                        </Stack>
                      </Tooltip>
                    </TableCell>
                  );
                }))}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {pagedPosts.length === 0 && !rowSections?.length ? (
              <TableRow>
                <TableCell colSpan={emptyColSpan} align="center" sx={{ py: 6 }}>
                  <Box
                    component="img"
                    src="/catchmoments/no-data.png"
                    alt="no data"
                    sx={{ width: '100%', maxWidth: 360, opacity: 0.8, mb: 2 }}
                  />
                </TableCell>
              </TableRow>
            ) : (
              <>
                {rowSections ? renderSectionRows(rowSections) : pagedPosts.map((post, idx) => renderPostRow(post, idx, (page - 1) * perPage + idx + 1, 'post'))}
                {totalRow && renderSummaryRow('total', totalRow.label, totalRow.summary, { total: true })}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 상세 모달 */}
      {ownDetail && detail === 'media' && (
        <PostMediaDetail
          post={selectedPost}
          posts={pagedPosts}
          open={isDetailOpen}
          onClose={handleDetailClose}
          searchKeyword={searchKeyword}
          clientSearch={clientSearchJoined}
          isBookmark={isBookmark}
          enableCampaignAdd={enableCampaignAdd}
          detailUsesMetricDetailSort={detailUsesMetricDetailSort}
        />
      )}
      {selectedPost && detail === 'post' && (
        <PostDetail
          post={selectedPost}
          open={isDetailOpen}
          onClose={handleDetailClose}
          searchKeyword={searchKeyword}
          clientSearch={clientSearchJoined}
          isBookmark={isBookmark}
          enableCampaignAdd={enableCampaignAdd}
          detailUsesMetricDetailSort={detailUsesMetricDetailSort}
        />
      )}

      <Dialog
        open={isVideoCheckDialogOpen}
        onClose={handleCloseVideoCheckDialog}
        fullWidth
        maxWidth="md"
        scroll="paper"
      >
        <DialogTitle sx={{ pb: 1 }}>
          영상 평가 결과
        </DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          {!hasRenderableEvidence(videoCheckDialogEvidence) && videoCheckDialogResult.length === 0 ? (
            <Typography sx={{ color: 'text.secondary' }}>표시할 평가 결과가 없습니다.</Typography>
          ) : (
            <Stack spacing={2}>
              {videoCheckDialogResult.length > 0 ? (
                <Stack spacing={1.5}>
                  {videoCheckDialogResult.map((item, index) => {
                    const q = String(item?.q ?? '').trim();
                    const verdict = String(item?.a?.verdict ?? '').trim();
                    const passed = Boolean(item?.a?.passed);
                    const note = String(item?.a?.note ?? '').trim();
                    const timecodes = renderEvidenceTimecodes(item?.a?.evidenceTimecodes);

                    return (
                      <Paper key={`video-check-item-${index}`} variant="outlined" sx={{ p: 1.5, borderRadius: RADIUS.md }}>
                        <Stack spacing={1}>
                          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                            <Stack direction="row" spacing={1} alignItems="center">
                              {renderPassedDot(passed, index)}
                              <Typography sx={{ typography: 'uiBody', fontWeight: 600 }}>
                                {verdict || (passed ? 'O' : 'X')}
                              </Typography>
                            </Stack>
                            <Typography sx={{ typography: 'uiMeta', color: passed ? 'success.main' : 'error.main', fontWeight: 600 }}>
                              {passed ? '통과' : '실패'}
                            </Typography>
                          </Stack>

                          <Typography sx={{ typography: 'uiStrong', color: 'text.primary' }}>
                            {q || `항목 ${index + 1}`}
                          </Typography>

                          {note ? (
                            <Typography sx={{ typography: 'uiBody', color: 'text.secondary', whiteSpace: 'pre-line' }}>
                              {note}
                            </Typography>
                          ) : null}

                          {timecodes ? (
                            <Stack sx={{ px: 1, py: 0.5, bgcolor: 'grey.200', borderRadius: RADIUS.sm }}>
                              <Typography sx={{ typography: 'uiMeta' }}>
                                근거 타임코드: {timecodes}
                              </Typography>
                            </Stack>
                          ) : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              ) : null}
              {hasRenderableEvidence(videoCheckDialogEvidence) && (
                <Accordion
                  defaultExpanded={false}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: RADIUS.md,
                    overflow: 'hidden',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: 'text.secondary' }} />}
                    aria-controls="video-check-evidence-content"
                    id="video-check-evidence-header"
                    sx={{
                      px: 1.5,
                      minHeight: 48,
                      bgcolor: 'grey.100',
                      '& .MuiAccordionSummary-content': { my: 1 },
                    }}
                  >
                    <Typography sx={{ typography: 'uiBody', fontWeight: 600 }}>
                      영상 정보
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ px: 1.5, pb: 1.5, pt: 0, bgcolor: 'grey.50' }}>
                    <Stack spacing={1.5}>
                      {String(videoCheckDialogEvidence.audioTranscript ?? '').trim() ? (
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            오디오 대본
                          </Typography>
                          <Typography
                            sx={{
                              typography: 'uiBody',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxHeight: 220,
                              overflow: 'auto',
                              color: 'text.primary',
                            }}
                          >
                            {videoCheckDialogEvidence.audioTranscript}
                          </Typography>
                        </Stack>
                      ) : null}
                      {String(videoCheckDialogEvidence.overlayTexts ?? '').trim() ? (
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            오버레이 텍스트
                          </Typography>
                          <Typography
                            sx={{
                              typography: 'uiBody',
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              maxHeight: 180,
                              overflow: 'auto',
                              color: 'text.primary',
                            }}
                          >
                            {videoCheckDialogEvidence.overlayTexts}
                          </Typography>
                        </Stack>
                      ) : null}
                      {String(videoCheckDialogEvidence.products ?? '').trim() ? (
                        <Stack spacing={0.5}>
                          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                            노출 제품
                          </Typography>
                          <Typography sx={{ typography: 'uiBody', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
                            {videoCheckDialogEvidence.products}
                          </Typography>
                        </Stack>
                      ) : null}
                      {(videoCheckDialogEvidence.lastCheckedAt != null ||
                        String(videoCheckDialogEvidence.lastStatus ?? '').trim() ||
                        String(videoCheckDialogEvidence.lastCheckedByUserId ?? '').trim()) && (
                          <Stack
                            direction="row"
                            spacing={1}
                            flexWrap="wrap"
                            sx={{ pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}
                          >
                            {videoCheckDialogEvidence.lastCheckedAt != null ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                최종 확인: {formatEvidenceTimestamp(videoCheckDialogEvidence.lastCheckedAt)}
                              </Typography>
                            ) : null}
                            {String(videoCheckDialogEvidence.lastStatus ?? '').trim() ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                상태: {videoCheckDialogEvidence.lastStatus}
                              </Typography>
                            ) : null}
                            {String(videoCheckDialogEvidence.lastCheckedByUserId ?? '').trim() ? (
                              <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
                                확인자: {videoCheckDialogEvidence.lastCheckedByUserId}
                              </Typography>
                            ) : null}
                          </Stack>
                        )}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={handleCloseVideoCheckDialog} aria-label="닫기">
            <Iconify icon="solar:close-circle-linear" width={20} />
          </IconButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** 계정 칸 앞 얼굴 — face 가 undefined 면(얼굴을 묻지 않는 화면) 감싸지 않고 글자만 그대로 */
function AccountFaceFrame({ face, channel, children }: { face: string | null | undefined; channel?: string; children: React.ReactNode }) {
  if (face === undefined) return <>{children}</>;
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
      <AccountAvatar src={face} channel={channel} size={24} />
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Stack>
  );
}
